import { useRecoilValue } from 'recoil';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useComparisonImages } from '~/hooks/viewer/useComparisonImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { replanningRunOutputsState } from '~/state/atoms/adaptiveReplanningAtoms';
import {
    currentStructureSetState,
    structureSetReadModelFamily,
} from '~/state/atoms/structureSet/structureSetAtoms';

export interface ReplanningStructureSetViews {
    readonly source: StructureSetReadModel | undefined;
    readonly propagated: StructureSetReadModel | undefined;
}

export function useReplanningStructureSets(): ReplanningStructureSetViews {
    const recorded = useRecoilValue(replanningRunOutputsState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const primaryImage = useSelectedPrimaryImage();
    const compared = useComparisonImages();
    const patient = useSelectedPatient();

    // Subscribed, not snapshot-read: a sign-off must not report approval a render late.
    const recordedSource = useRecoilValue(
        structureSetReadModelFamily(recorded.sourceUid ?? '')
    );
    const recordedPropagated = useRecoilValue(
        structureSetReadModelFamily(recorded.propagatedUid ?? '')
    );

    const heldByPatient = (set: StructureSetReadModel | undefined) =>
        set &&
        patient?.structureSets.some((held) => held.dicomUid === set.dicomUid)
            ? set
            : undefined;

    const propagated = heldByPatient(recordedPropagated);
    const source =
        heldByPatient(recordedSource) ??
        // Never the propagated set, settled or still being produced: reading a
        // run's output as input compounds the transform.
        (currentStructureSet?.dicomUid !== recorded.propagatedUid &&
        currentStructureSet?.dicomUid !== recorded.pendingPropagatedUid
            ? currentStructureSet
            : undefined);

    const onScreen =
        propagated &&
        [primaryImage, ...compared].some(
            (image) => image?.dicomUid === propagated.imageUid
        );

    return { source, propagated: onScreen ? propagated : undefined };
}
